import { MEDIA_QUERIES, type Breakpoint } from '../constants/breakpoints';
import { useMediaQuery } from './useMediaQuery';

/**
 * Result of the useBreakpoint hook
 */
export interface BreakpointResult {
  /** Current breakpoint: 'mobile' | 'tablet' | 'desktop' */
  breakpoint: Breakpoint;
  /** true if viewport < 640px */
  isMobile: boolean;
  /** true if 640px <= viewport < 1024px */
  isTablet: boolean;
  /** true if viewport >= 1024px */
  isDesktop: boolean;
}

/**
 * Hook to detect current responsive breakpoint
 *
 * Uses window.matchMedia API for efficient, event-based detection.
 * Automatically updates when viewport crosses breakpoint thresholds.
 * SSR-safe: defaults to mobile breakpoint when window is not available.
 *
 * Breakpoints match Tailwind CSS configuration:
 * - mobile: 0-639px
 * - tablet: 640-1023px (Tailwind 'sm' and up)
 * - desktop: 1024px+ (Tailwind 'lg' and up)
 *
 * @returns BreakpointResult with current breakpoint and convenience booleans
 *
 * @example Basic usage
 * ```tsx
 * function MyComponent() {
 *   const { isMobile, isDesktop } = useBreakpoint();
 *
 *   return (
 *     <div>
 *       {isMobile && <MobileNav />}
 *       {isDesktop && <DesktopSidebar />}
 *     </div>
 *   );
 * }
 * ```
 *
 * @example Using breakpoint value
 * ```tsx
 * function MyComponent() {
 *   const { breakpoint } = useBreakpoint();
 *
 *   const columns = {
 *     mobile: 1,
 *     tablet: 2,
 *     desktop: 4,
 *   }[breakpoint];
 *
 *   return <Grid columns={columns} />;
 * }
 * ```
 */
export function useBreakpoint(): BreakpointResult {
  const isDesktop = useMediaQuery(MEDIA_QUERIES.desktop);
  const isTablet = useMediaQuery(MEDIA_QUERIES.tablet);

  // Determine breakpoint: desktop takes priority, then tablet, then mobile
  const breakpoint: Breakpoint = isDesktop
    ? 'desktop'
    : isTablet
      ? 'tablet'
      : 'mobile';

  return {
    breakpoint,
    isMobile: breakpoint === 'mobile',
    isTablet: breakpoint === 'tablet',
    isDesktop,
  };
}
